import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { gtmNewsletterSubmit } from '../../utils/gtm';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Checkbox,
  LinearProgress,
  Tooltip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Description,
  AccountBalance,
  CalendarMonth,
  CalendarToday,
  School,
  PersonAdd,
  ArrowForward,
  CheckCircleOutline,
  CreditScore,
  AccountBalanceWallet,
  Home,
  Download as DownloadIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  SavingsOutlined as PiggyBankIcon,
  WorkOutline as BriefcaseIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../../components/SEO/SEOHead';
import PageHeader from '../../components/Layout/PageHeader';
import NewsletterSection from '../../components/Layout/NewsletterSection';
import TrustStrip from '../../components/Layout/TrustStrip';
import RelatedArticles from '../../components/Blog/RelatedArticles';
import { useTranslation } from 'react-i18next';

/* ─── Helpers ────────────────────────────────────────────────── */

function getDaysUntil(target) {
  const diff = target - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function estimateRefund(income, hasRent, hasRRSP, t) {
  const n = parseFloat(income) || 0;
  if (n <= 0) return null;
  const breakdown = [];

  const personal = n <= 55000 ? 800 : n <= 75000 ? 500 : 300;
  breakdown.push({ label: t('blog.guiaImpuestosQC.estimator.personalCredits'), amount: personal });

  if (hasRent) breakdown.push({ label: t('blog.guiaImpuestosQC.estimator.solidarityRent'), amount: 960 });
  if (hasRRSP) breakdown.push({ label: t('blog.guiaImpuestosQC.estimator.rrspBenefit'), amount: Math.round(n * 0.015) });
  if (n <= 50000) breakdown.push({ label: t('blog.guiaImpuestosQC.estimator.gstCredit'), amount: 533 });
  else if (n <= 65000) breakdown.push({ label: t('blog.guiaImpuestosQC.estimator.gstCredit'), amount: 300 });

  const total = breakdown.reduce((s, i) => s + i.amount, 0);
  return { breakdown, total };
}

/** Generate a basic PDF-like text blob for download */
function generateChecklistText(docs, checkedDocs, t) {
  const lines = [
    t('blog.guiaImpuestosQC.docs.heading'),
    '═'.repeat(40),
    '',
    ...docs.map((doc) => `${checkedDocs[doc.key] ? '[x]' : '[ ]'} ${doc.label} — ${doc.desc}`),
    '',
    '─'.repeat(40),
    'FinMaple.com — ' + t('blog.guiaImpuestosQC.sources.updated'),
  ];
  return lines.join('\n');
}

/* ─── Shared styles ──────────────────────────────────────────── */

const cardSx = {
  height: '100%',
  borderRadius: '16px',
  border: '1px solid #E6E9EE',
  boxShadow: 'none',
  transition: 'box-shadow 0.2s',
  '&:hover': { boxShadow: '0 6px 18px rgba(0,0,0,0.05)' },
};

const sectionCard = {
  bgcolor: '#FFFFFF',
  borderRadius: '16px',
  border: '1px solid #E6E9EE',
  boxShadow: 'none',
  p: { xs: 3, md: 4 },
};

const contentParagraph = {
  color: '#5F6B7A',
  lineHeight: 1.8,
  mb: 2,
  maxWidth: '75ch',
};

const sectionHeading = {
  color: '#1A1A1A',
  fontWeight: 600,
  mb: 2.5,
};

/* h6 variant handles font-size (1.25rem/20px per MUI default).
   We only add spacing; color inherits from textPrimary in theme. */
const subHeading = {
  fontWeight: 600,
  color: '#1A1A1A',
  mb: 1,
  mt: 3,
};

const bulletList = {
  pl: 2,
  mb: 2,
  '& li': { mb: 0.5, color: '#5F6B7A', lineHeight: 1.7 },
};

/* ─── TOC Items ──────────────────────────────────────────────── */

const TOC_ITEMS = [
  { id: 'calendario', key: 'calendar' },
  { id: 'documentos', key: 'docs' },
  { id: 'primera-vez', key: 'firstTime' },
  { id: 'como-declarar', key: 'howTo' },
  { id: 'creditos', key: 'credits' },
  { id: 'ingresos-exterior', key: 'foreignIncome' },
  { id: 'errores', key: 'mistakes' },
  { id: 'reembolso', key: 'refund' },
  { id: 'faq', key: 'faq' },
];

/* ─── Component ──────────────────────────────────────────────── */

const GuiaImpuestosQuebecPage = () => {
  const { t, i18n } = useTranslation();
  const { lng } = useParams();

  /* ── Schema (Article + HowTo + FAQ) ── */
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('blog.guiaImpuestosQC.seo.title'),
    description: t('blog.guiaImpuestosQC.seo.description'),
    datePublished: '2026-02-21',
    dateModified: '2026-03-07',
    author: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
    publisher: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com', logo: { '@type': 'ImageObject', url: 'https://www.finmaple.com/og-image.jpg' } },
    speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.article-headline', '.quick-answer'] },
    inLanguage: i18n.language,
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t('blog.guiaImpuestosQC.howToSchema.name'),
    step: [1, 2, 3, 4, 5, 6].map((n) => ({
      '@type': 'HowToStep',
      name: t(`blog.guiaImpuestosQC.howToSchema.step${n}Name`),
      text: t(`blog.guiaImpuestosQC.howToSchema.step${n}Text`),
    })),
  };

  const faqItems = useMemo(() => [1, 2, 3, 4, 5, 6].map((n) => ({
    question: t(`blog.guiaImpuestosQC.faq.q${n}`),
    answer: t(`blog.guiaImpuestosQC.faq.a${n}`),
  })), [t]);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };

  /* ── Data ── */
  const SPOKE_CARDS = [
    {
      icon: <Description sx={{ fontSize: 32 }} />,
      title: t('blog.guiaImpuestosQC.spokeCards.t4Title'),
      desc: t('blog.guiaImpuestosQC.spokeCards.t4Desc'),
      to: '/blog/t4-explicado-espanol-impuestos-canada',
      chipLabel: t('blog.guiaImpuestosQC.spokeCards.t4Chip'),
      chipBg: '#E8F5E9',
      chipColor: '#2E7D32',
    },
    {
      icon: <AccountBalance sx={{ fontSize: 32 }} />,
      title: t('blog.guiaImpuestosQC.spokeCards.solidarityTitle'),
      desc: t('blog.guiaImpuestosQC.spokeCards.solidarityDesc'),
      to: '/blog/solidarity-tax-credit-quebec-guia-completa',
      chipLabel: t('blog.guiaImpuestosQC.spokeCards.solidarityChip'),
      chipBg: '#FFEBEE',
      chipColor: '#C62828',
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 32 }} />,
      title: t('blog.guiaImpuestosQC.spokeCards.rrspTitle'),
      desc: t('blog.guiaImpuestosQC.spokeCards.rrspDesc'),
      to: '/financlopedia/rrsp-canada',
      chipLabel: t('blog.guiaImpuestosQC.spokeCards.rrspChip'),
      chipBg: '#FFF8E1',
      chipColor: '#7D5A00',
    },
    {
      icon: <School sx={{ fontSize: 32 }} />,
      title: t('blog.guiaImpuestosQC.spokeCards.craTitle'),
      desc: t('blog.guiaImpuestosQC.spokeCards.craDesc'),
      to: '/financlopedia/cra-canada',
      chipLabel: t('blog.guiaImpuestosQC.spokeCards.craChip'),
      chipBg: '#F7F9FC',
      chipColor: '#1E3A5F',
    },
    {
      icon: <PersonAdd sx={{ fontSize: 32 }} />,
      title: t('blog.guiaImpuestosQC.spokeCards.firstTitle'),
      desc: t('blog.guiaImpuestosQC.spokeCards.firstDesc'),
      to: '/primeros-pasos',
      chipLabel: t('blog.guiaImpuestosQC.spokeCards.firstChip'),
      chipBg: '#F7F9FC',
      chipColor: '#5F6B7A',
    },
  ];

  const DOCS_CHECKLIST = [
    { key: 't4', label: 'T4', desc: t('blog.guiaImpuestosQC.docs.t4') },
    { key: 'rl1', label: 'RL-1', desc: t('blog.guiaImpuestosQC.docs.rl1') },
    { key: 'rl31', label: 'RL-31', desc: t('blog.guiaImpuestosQC.docs.rl31') },
    { key: 't5', label: 'T5', desc: t('blog.guiaImpuestosQC.docs.t5') },
    { key: 'rrsp', label: t('blog.guiaImpuestosQC.docs.rrspLabel'), desc: t('blog.guiaImpuestosQC.docs.rrsp') },
    { key: 'donaciones', label: t('blog.guiaImpuestosQC.docs.donationsLabel'), desc: t('blog.guiaImpuestosQC.docs.donations') },
    { key: 'medicos', label: t('blog.guiaImpuestosQC.docs.medicalLabel'), desc: t('blog.guiaImpuestosQC.docs.medical') },
  ];

  const CALENDAR_ITEMS = [
    {
      icon: <PiggyBankIcon sx={{ fontSize: 20 }} />,
      date: t('blog.guiaImpuestosQC.timeline.mar3'),
      label: t('blog.guiaImpuestosQC.timeline.mar3Desc'),
      target: new Date(2026, 2, 2),
      tag: 'RRSP',
    },
    {
      icon: <Description sx={{ fontSize: 20 }} />,
      date: t('blog.guiaImpuestosQC.timeline.apr30'),
      label: t('blog.guiaImpuestosQC.timeline.apr30Desc'),
      target: new Date(2026, 3, 30),
      tag: 'CRA + RQ',
      highlight: true,
    },
    {
      icon: <Description sx={{ fontSize: 20 }} />,
      date: t('blog.guiaImpuestosQC.timeline.apr30'),
      label: t('blog.guiaImpuestosQC.timeline.apr30Desc'),
      target: new Date(2026, 3, 30),
      tag: 'Revenu QC',
      highlight: true,
    },
    {
      icon: <BriefcaseIcon sx={{ fontSize: 20 }} />,
      date: t('blog.guiaImpuestosQC.timeline.jun15'),
      label: t('blog.guiaImpuestosQC.timeline.jun15Desc'),
      target: new Date(2026, 5, 15),
      tag: 'Self-employed',
      tooltip: t('blog.guiaImpuestosQC.timeline.selfEmployedTooltip'),
    },
  ];

  const NEXT_READS = [
    {
      icon: <CreditScore sx={{ fontSize: 28 }} />,
      title: t('blog.guiaImpuestosQC.nextReads.creditTitle'),
      desc: t('blog.guiaImpuestosQC.nextReads.creditDesc'),
      to: '/blog/construir-credito-canada-desde-cero',
    },
    {
      icon: <AccountBalanceWallet sx={{ fontSize: 28 }} />,
      title: t('blog.guiaImpuestosQC.nextReads.accountsTitle'),
      desc: t('blog.guiaImpuestosQC.nextReads.accountsDesc'),
      to: '/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes',
    },
    {
      icon: <Home sx={{ fontSize: 28 }} />,
      title: t('blog.guiaImpuestosQC.nextReads.mortgageTitle'),
      desc: t('blog.guiaImpuestosQC.nextReads.mortgageDesc'),
      to: '/blog/primera-hipoteca-canada-guia',
    },
  ];

  /* ── Interactive doc checklist ── */
  const [checkedDocs, setCheckedDocs] = useState({});
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('finmaple_quebec_docs') || '{}');
      if (Object.keys(saved).length) setCheckedDocs(saved);
    } catch { /* ignore */ }
  }, []);
  const toggleDoc = useCallback((key) => {
    setCheckedDocs(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('finmaple_quebec_docs', JSON.stringify(next));
      return next;
    });
  }, []);
  const checkedCount = Object.values(checkedDocs).filter(Boolean).length;

  /* ── Checklist actions ── */
  const handleDownloadChecklist = () => {
    const text = generateChecklistText(DOCS_CHECKLIST, checkedDocs, t);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'finmaple-checklist-impuestos-quebec.txt';
    link.click();
    URL.revokeObjectURL(link.href);
    if (window.plausible) window.plausible('PDF Download', { props: { document: 'checklist-impuestos-quebec' } });
    if (window.dataLayer) window.dataLayer.push({ event: 'pdf_download', document: 'checklist-impuestos-quebec' });
  };

  const [emailInput, setEmailInput] = useState('');
  const [emailStatus, setEmailStatus] = useState(null); // null | 'sent' | 'error'
  const handleSendEmail = async () => {
    if (!emailInput) return;
    try {
      const res = await fetch('https://buttondown.com/api/emails/embed-subscribe/finmaple', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: emailInput, tag: 'checklist-impuestos-quebec' }),
      });
      if (res.ok || res.status === 303) {
        setEmailStatus('sent');
        if (window.plausible) window.plausible('Email Capture', { props: { source: 'checklist-impuestos-quebec' } });
        gtmNewsletterSubmit({ source: 'checklist-impuestos-quebec' });
      } else {
        setEmailStatus('error');
      }
    } catch {
      setEmailStatus('error');
    }
  };

  /* ── Estimator ── */
  const [estIncome, setEstIncome] = useState('');
  const [estRent, setEstRent] = useState(false);
  const [estRRSP, setEstRRSP] = useState(false);
  const [estResult, setEstResult] = useState(null);

  /* ── TOC — sticky with active highlight ── */
  const [activeSection, setActiveSection] = useState('');
  const [tocOpen, setTocOpen] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    const handleScroll = () => {
      const offset = 120;
      let current = '';
      for (const item of TOC_ITEMS) {
        const el = sectionRefs.current[item.id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= offset) current = item.id;
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const setSectionRef = useCallback((id) => (el) => {
    sectionRefs.current[id] = el;
  }, []);

  /* ── Credits summary table data ── */
  const CREDITS_TABLE = [
    { credit: 'tableGst', amt: 'tableGstAmt', req: 'tableGstReq', freq: 'tableGstFreq' },
    { credit: 'tableCcb', amt: 'tableCcbAmt', req: 'tableCcbReq', freq: 'tableCcbFreq' },
    { credit: 'tableSolidarity', amt: 'tableSolidarityAmt', req: 'tableSolidarityReq', freq: 'tableSolidarityFreq' },
    { credit: 'tableMedical', amt: 'tableMedicalAmt', req: 'tableMedicalReq', freq: 'tableMedicalFreq' },
    { credit: 'tableMoving', amt: 'tableMovingAmt', req: 'tableMovingReq', freq: 'tableMovingFreq' },
  ];

  return (
    <Box component="article" sx={{ bgcolor: '#F7F9FC' }}>
      <SEOHead
        title={t('blog.guiaImpuestosQC.seo.title')}
        description={t('blog.guiaImpuestosQC.seo.description')}
        canonical="/blog/guia-impuestos-latinos-quebec-2026"
        routeKey="blogGuiaQuebec"
        seoKey="blogGuiaQuebec"
        noIndex={i18n.language !== 'es'}
        ogImage="https://www.finmaple.com/images/blog/guia-impuestos-quebec-latinos.jpg"
        schema={articleSchema}
      />

      {/* HowTo + FAQ schemas */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* ── Hero ── */}
      <PageHeader
        variant="functional"
        title={t('blog.guiaImpuestosQC.hero.title')}
        subtitle={t('blog.guiaImpuestosQC.hero.subtitle')}
        breadcrumbs={[{ label: t('blog.guiaImpuestosQC.hero.breadcrumbGuides'), to: `/${lng}/blog` }, { label: t('blog.guiaImpuestosQC.hero.breadcrumbThis') }]}
        maxWidth="lg"
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2.5, mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#5F6B7A' }}>
            <CalendarToday sx={{ fontSize: 14 }} />
            <Typography variant="body2">{t('blog.guiaImpuestosQC.hero.date')}</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#5F6B7A' }}>{t('blog.guiaImpuestosQC.hero.author')}</Typography>
        </Box>
      </PageHeader>

      <TrustStrip />

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Grid container spacing={4}>

          {/* ── TOC sidebar (desktop) ── */}
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              component="nav"
              aria-label={t('blog.guiaImpuestosQC.toc.title')}
              sx={{
                position: 'sticky',
                top: 90,
                maxHeight: 'calc(100vh - 120px)',
                overflowY: 'auto',
              }}
            >
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#1A1A1A', mb: 1.5 }}>
                {t('blog.guiaImpuestosQC.toc.title')}
              </Typography>
              {TOC_ITEMS.map((item) => (
                <Box
                  key={item.id}
                  component="a"
                  href={`#${item.id}`}
                  sx={{
                    display: 'block',
                    py: 0.5,
                    px: 1.5,
                    fontSize: '0.8125rem',
                    color: activeSection === item.id ? '#1E3A5F' : '#5F6B7A',
                    fontWeight: activeSection === item.id ? 600 : 400,
                    borderLeft: activeSection === item.id ? '2px solid #1E3A5F' : '2px solid transparent',
                    textDecoration: 'none',
                    transition: 'all 0.15s',
                    '&:hover': { color: '#1E3A5F' },
                  }}
                >
                  {t(`blog.guiaImpuestosQC.toc.${item.key}`)}
                </Box>
              ))}
            </Box>
          </Grid>

          {/* ── TOC mobile (collapsible) ── */}
          <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
            <Box sx={{ ...sectionCard, p: 2, mb: 2 }}>
              <Box
                onClick={() => setTocOpen(!tocOpen)}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              >
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>
                  {t('blog.guiaImpuestosQC.toc.title')}
                </Typography>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: '#5F6B7A',
                    transition: 'transform 0.2s',
                    transform: tocOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </Box>
              <Collapse in={tocOpen}>
                <Box sx={{ mt: 1 }}>
                  {TOC_ITEMS.map((item) => (
                    <Box
                      key={item.id}
                      component="a"
                      href={`#${item.id}`}
                      onClick={() => setTocOpen(false)}
                      sx={{
                        display: 'block',
                        py: 0.5,
                        fontSize: '0.8125rem',
                        color: '#5F6B7A',
                        textDecoration: 'none',
                        '&:hover': { color: '#1E3A5F' },
                      }}
                    >
                      {t(`blog.guiaImpuestosQC.toc.${item.key}`)}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Grid>

          {/* ── Main content column ── */}
          <Grid item xs={12} md={9}>

            {/* ── Quick Answer (Fix 2: bullets) ── */}
            <Box
              className="quick-answer"
              sx={{
                ...sectionCard,
                mb: 3,
                borderLeft: '4px solid #1E3A5F',
              }}
            >
              <Box component="ul" sx={{ pl: 2, m: 0, '& li': { mb: 0.75, color: '#1A1A1A', fontSize: '0.9375rem', lineHeight: 1.6 } }}>
                <li>{t('blog.guiaImpuestosQC.quickAnswer.dual')}</li>
                <li>{t('blog.guiaImpuestosQC.quickAnswer.docs')}</li>
                <li>{t('blog.guiaImpuestosQC.quickAnswer.benefits')}</li>
                <li>{t('blog.guiaImpuestosQC.quickAnswer.firstTime')}</li>
              </Box>
              <RouterLink
                to={`/${lng}/blog/cra-vs-revenu-quebec-diferencias`}
                style={{ color: '#1E3A5F', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none', display: 'inline-block', marginTop: 8 }}
              >
                {t('blog.guiaImpuestosQC.quickAnswer.learnMore')}
              </RouterLink>
            </Box>

            {/* ── Intro ── */}
            <Typography variant="body1" sx={{ ...contentParagraph, mb: 4 }}>
              {t('blog.guiaImpuestosQC.intro.before')}<RouterLink to={`/${lng}/blog/cra-vs-revenu-quebec-diferencias`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.guiaImpuestosQC.intro.linkCRA')}</RouterLink>{t('blog.guiaImpuestosQC.intro.middle')}<RouterLink to={`/${lng}/blog/t4-explicado-espanol-impuestos-canada`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.guiaImpuestosQC.intro.linkT4')}</RouterLink>{t('blog.guiaImpuestosQC.intro.after')}
            </Typography>

            {/* ── Spoke Cards ── */}
            <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mb: 2.5 }}>
              {t('blog.guiaImpuestosQC.spokeCards.heading')}
            </Typography>
            <Grid container spacing={2.5} sx={{ mb: 6 }}>
              {SPOKE_CARDS.map((card) => (
                <Grid item xs={12} sm={6} md={4} key={card.title}>
                  <Card sx={cardSx}>
                    <CardActionArea
                      component={RouterLink}
                      to={`/${lng}${card.to}`}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ color: '#1E3A5F' }}>{card.icon}</Box>
                          <Chip
                            label={card.chipLabel}
                            size="small"
                            sx={{ bgcolor: card.chipBg, color: card.chipColor, fontWeight: 500, fontSize: '0.75rem', height: 22, border: 'none' }}
                          />
                        </Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.75, lineHeight: 1.35 }}>
                          {card.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 1.5 }}>
                          {card.desc}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                          {t('blog.guiaImpuestosQC.spokeCards.viewGuide')} <ArrowForward sx={{ fontSize: 14 }} />
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* ══════════════════════════════════════════════════════════
                §1 — Tax Calendar (Fix 1: replaces deadline chips)
            ══════════════════════════════════════════════════════════ */}
            <Box id="calendario" ref={setSectionRef('calendario')} sx={{ ...sectionCard, mb: 6 }}>
              <Typography variant="h4" component="h2" sx={{ ...sectionHeading }}>
                {t('blog.guiaImpuestosQC.timeline.heading')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 0, sm: 0 },
                  position: 'relative',
                }}
              >
                {CALENDAR_ITEMS.map((item, idx) => {
                  const isPast = new Date() > item.target;
                  const daysLeft = getDaysUntil(item.target);
                  const dotColor = isPast ? '#E6E9EE' : item.highlight ? '#C62828' : '#1E3A5F';
                  return (
                    <Box
                      key={idx}
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        position: 'relative',
                        py: 1.5,
                        px: 1,
                        opacity: isPast ? 0.5 : 1,
                      }}
                    >
                      {/* Connector line (desktop) */}
                      {idx > 0 && (
                        <Box
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            position: 'absolute',
                            top: '28px',
                            left: 0,
                            right: '50%',
                            height: 2,
                            bgcolor: '#E6E9EE',
                          }}
                        />
                      )}
                      {idx < CALENDAR_ITEMS.length - 1 && (
                        <Box
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            position: 'absolute',
                            top: '28px',
                            left: '50%',
                            right: 0,
                            height: 2,
                            bgcolor: '#E6E9EE',
                          }}
                        />
                      )}
                      {/* Icon circle */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          bgcolor: isPast ? '#F7F9FC' : `${dotColor}10`,
                          border: `2px solid ${dotColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 1,
                          position: 'relative',
                          zIndex: 1,
                          color: isPast ? '#E6E9EE' : dotColor,
                        }}
                      >
                        {item.icon}
                      </Box>
                      {/* Date */}
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 700,
                          color: isPast ? '#5F6B7A' : '#1A1A1A',
                          textDecoration: isPast ? 'line-through' : 'none',
                          mb: 0.25,
                        }}
                      >
                        {item.date}
                      </Typography>
                      {/* Label */}
                      <Tooltip title={item.tooltip || ''} arrow>
                        <Typography sx={{ fontSize: '12px', color: '#5F6B7A', lineHeight: 1.4, mb: 0.5 }}>
                          {item.label}
                        </Typography>
                      </Tooltip>
                      {/* Tag + days left */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Chip
                          label={item.tag}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: isPast ? '#F7F9FC' : item.highlight ? '#FFEBEE' : '#F7F9FC',
                            color: isPast ? '#5F6B7A' : item.highlight ? '#C62828' : '#1E3A5F',
                            border: 'none',
                          }}
                        />
                        {!isPast && daysLeft > 0 && (
                          <Chip
                            label={t('blog.guiaImpuestosQC.timeline.daysLeft', { days: daysLeft })}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: daysLeft <= 30 ? '#FFEBEE' : '#FFF8E1',
                              color: daysLeft <= 30 ? '#C62828' : '#7D5A00',
                              border: 'none',
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Button
                component={RouterLink}
                to={`/${lng}/calendario-fiscal-2026`}
                endIcon={<ArrowForward />}
                sx={{ mt: 2, color: '#1E3A5F', textTransform: 'none', fontWeight: 500 }}
              >
                {t('blog.guiaImpuestosQC.timeline.viewCalendar')}
              </Button>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §2 — Document Checklist (Fix 3: download + email)
            ══════════════════════════════════════════════════════════ */}
            <Box id="documentos" ref={setSectionRef('documentos')} sx={{ ...sectionCard, mb: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A' }}>
                  {t('blog.guiaImpuestosQC.docs.heading')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5F6B7A', fontWeight: 600 }}>
                  {checkedCount} {t('blog.guiaImpuestosQC.docs.of')} {DOCS_CHECKLIST.length}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(checkedCount / DOCS_CHECKLIST.length) * 100}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  mb: 2.5,
                  bgcolor: '#E6E9EE',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    bgcolor: checkedCount === DOCS_CHECKLIST.length ? '#2E7D32' : '#1E3A5F',
                  },
                }}
              />
              {DOCS_CHECKLIST.map((doc) => (
                <Box
                  key={doc.key}
                  onClick={() => toggleDoc(doc.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1.25,
                    px: 1,
                    mx: -1,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: '#F7F9FC' },
                    '&:not(:last-child)': { borderBottom: '1px solid #F7F9FC' },
                  }}
                >
                  <Checkbox
                    checked={!!checkedDocs[doc.key]}
                    size="small"
                    sx={{
                      p: 0.25,
                      color: '#E6E9EE',
                      '&.Mui-checked': { color: '#2E7D32' },
                    }}
                    tabIndex={-1}
                  />
                  <Box sx={{ pt: 0.1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: checkedDocs[doc.key] ? '#5F6B7A' : '#1A1A1A',
                        textDecoration: checkedDocs[doc.key] ? 'line-through' : 'none',
                        transition: 'color 0.15s',
                      }}
                    >
                      {doc.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#5F6B7A', lineHeight: 1.4 }}>
                      {doc.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {checkedCount === DOCS_CHECKLIST.length && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2, color: '#2E7D32' }}>
                  <CheckCircleOutline sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('blog.guiaImpuestosQC.docs.allReady')}
                  </Typography>
                </Box>
              )}
              {/* Checklist actions */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadChecklist}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', borderColor: '#E6E9EE', color: '#1A1A1A' }}
                >
                  {t('blog.guiaImpuestosQC.docs.download')}
                </Button>
                {emailStatus === 'sent' ? (
                  <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 500 }}>
                    {t('blog.guiaImpuestosQC.docs.emailSent')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder={t('blog.guiaImpuestosQC.docs.emailPlaceholder')}
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 34, fontSize: '0.8125rem' } }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleSendEmail}
                      disabled={!emailInput}
                      sx={{ color: '#1E3A5F' }}
                      aria-label={t('blog.guiaImpuestosQC.docs.email')}
                    >
                      <EmailIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                )}
                {emailStatus === 'error' && (
                  <Typography variant="caption" sx={{ color: '#C62828' }}>
                    {t('blog.guiaImpuestosQC.docs.emailError')}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §3 — Primera declaración (NEW Section A)
            ══════════════════════════════════════════════════════════ */}
            <Box id="primera-vez" ref={setSectionRef('primera-vez')} sx={{ ...sectionCard, mb: 6 }}>
              <Typography variant="h4" component="h2" sx={sectionHeading}>
                {t('blog.guiaImpuestosQC.firstTime.heading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.intro')}
              </Typography>

              {/* SIN */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.firstTime.sinHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.sinText')}
              </Typography>

              {/* Partial year */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.firstTime.partialHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.partialText')}
              </Typography>

              {/* CRA My Account */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.firstTime.craHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.craText')}
              </Typography>
              <Typography sx={{ ...contentParagraph, mb: 1 }}>
                {t('blog.guiaImpuestosQC.firstTime.craStepsIntro')}
              </Typography>
              <Box component="ol" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.firstTime.craStep1')}</li>
                <li>{t('blog.guiaImpuestosQC.firstTime.craStep2')}</li>
                <li>{t('blog.guiaImpuestosQC.firstTime.craStep3')}</li>
              </Box>
              <Typography sx={{ ...contentParagraph, fontSize: '0.8125rem' }}>
                {t('blog.guiaImpuestosQC.firstTime.craSecurityCode')}
              </Typography>
              <Box sx={{ bgcolor: '#E8F5E9', borderRadius: '8px', p: 2, mt: 1.5, mb: 2 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 500 }}>
                  <strong>Tip:</strong> {t('blog.guiaImpuestosQC.firstTime.craTip')}
                </Typography>
              </Box>

              {/* Partial vs full year */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.firstTime.yearHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.yearFull')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.yearPartial')}
              </Typography>
              <Box sx={{ bgcolor: '#FFF8E1', borderRadius: '8px', p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#7D5A00', fontWeight: 500 }}>
                  <strong>Quebec:</strong> {t('blog.guiaImpuestosQC.firstTime.yearQuebec')}
                </Typography>
              </Box>

              {/* No income */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.firstTime.noIncomeHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.firstTime.noIncomeText')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.firstTime.noIncomeBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.firstTime.noIncomeBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.firstTime.noIncomeBullet3')}</li>
                <li>{t('blog.guiaImpuestosQC.firstTime.noIncomeBullet4')}</li>
              </Box>
              <Box sx={{ bgcolor: '#FFEBEE', borderRadius: '8px', p: 2 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#C62828', fontWeight: 600 }}>
                  {t('blog.guiaImpuestosQC.firstTime.noIncomeWarning')}
                </Typography>
              </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §4 — How to file
            ══════════════════════════════════════════════════════════ */}
            <Box id="como-declarar" ref={setSectionRef('como-declarar')} sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mb: 2.5 }}>
                {t('blog.guiaImpuestosQC.howToFile.heading')}
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ ...sectionCard, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Chip
                      label={t('blog.guiaImpuestosQC.howToFile.freeChip')}
                      size="small"
                      sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 500, mb: 2, alignSelf: 'flex-start', border: 'none' }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
                      {t('blog.guiaImpuestosQC.howToFile.onlineTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.7, mb: 2 }}>
                      {t('blog.guiaImpuestosQC.howToFile.onlineDescBefore')}<RouterLink to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.guiaImpuestosQC.howToFile.onlineDescLink')}</RouterLink>{t('blog.guiaImpuestosQC.howToFile.onlineDescAfter')}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2.5, flexGrow: 1, '& li': { mb: 0.5, color: '#5F6B7A', fontSize: '0.875rem' } }}>
                      <li>{t('blog.guiaImpuestosQC.howToFile.onlineLi1')}</li>
                      <li>{t('blog.guiaImpuestosQC.howToFile.onlineLi2')}</li>
                      <li>{t('blog.guiaImpuestosQC.howToFile.onlineLi3')}</li>
                    </Box>
                    <Button
                      variant="contained"
                      href="https://www.wealthsimple.com/en-ca/tax"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      fullWidth
                      sx={{
                        bgcolor: '#1E3A5F',
                        color: '#fff',
                        fontWeight: 500,
                        borderRadius: '12px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#1A3152' },
                      }}
                    >
                      {t('blog.guiaImpuestosQC.howToFile.onlineCTA')}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ ...sectionCard, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Chip
                      label={t('blog.guiaImpuestosQC.howToFile.personalizedChip')}
                      size="small"
                      sx={{ bgcolor: '#F7F9FC', color: '#5F6B7A', fontWeight: 500, mb: 2, alignSelf: 'flex-start' }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
                      {t('blog.guiaImpuestosQC.howToFile.accountantTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.7, mb: 2 }}>
                      {t('blog.guiaImpuestosQC.howToFile.accountantDesc')}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2.5, flexGrow: 1, '& li': { mb: 0.5, color: '#5F6B7A', fontSize: '0.875rem' } }}>
                      <li>{t('blog.guiaImpuestosQC.howToFile.accountantLi1')}</li>
                      <li>{t('blog.guiaImpuestosQC.howToFile.accountantLi2')}</li>
                      <li>{t('blog.guiaImpuestosQC.howToFile.accountantLi3')}</li>
                    </Box>
                    <Button
                      component={RouterLink}
                      to={`/${lng}/contacto`}
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: '#E6E9EE',
                        color: '#1A1A1A',
                        fontWeight: 500,
                        borderRadius: '12px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#F7F9FC', borderColor: '#E6E9EE' },
                      }}
                    >
                      {t('blog.guiaImpuestosQC.howToFile.accountantCTA')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §5 — Créditos (NEW Section B)
            ══════════════════════════════════════════════════════════ */}
            <Box id="creditos" ref={setSectionRef('creditos')} sx={{ ...sectionCard, mb: 6 }}>
              <Typography variant="h4" component="h2" sx={sectionHeading}>
                {t('blog.guiaImpuestosQC.credits.heading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.credits.intro')}
              </Typography>

              {/* GST/HST */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.credits.gstHeading')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.credits.gstBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.gstBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.gstBullet3')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.gstBullet4')}</li>
              </Box>

              {/* CCB */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.credits.ccbHeading')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.credits.ccbBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.ccbBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.ccbBullet3')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.ccbBullet4')}</li>
              </Box>

              {/* Solidarity */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.credits.solidarityHeading')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.credits.solidarityBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.solidarityBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.solidarityBullet3')}</li>
              </Box>
              <RouterLink
                to={`/${lng}/blog/solidarity-tax-credit-quebec-guia-completa`}
                style={{ color: '#1E3A5F', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'underline', display: 'inline-block', marginBottom: 8 }}
              >
                {t('blog.guiaImpuestosQC.credits.solidarityLink')}
              </RouterLink>

              {/* CAI */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.credits.caiHeading')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.credits.caiBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.caiBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.caiBullet3')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.caiBullet4')}</li>
              </Box>

              {/* Medical */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.credits.medicalHeading')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.credits.medicalBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.medicalBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.medicalBullet3')}</li>
              </Box>

              {/* Moving */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.credits.movingHeading')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.credits.movingBullet1')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.movingBullet2')}</li>
                <li>{t('blog.guiaImpuestosQC.credits.movingBullet3')}</li>
              </Box>

              {/* Summary table */}
              <Typography variant="h6" component="h3" sx={{ ...subHeading, mt: 4 }}>
                {t('blog.guiaImpuestosQC.credits.tableHeading')}
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E6E9EE', borderRadius: '16px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#F7F9FC' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>{t('blog.guiaImpuestosQC.credits.tableCredit')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>{t('blog.guiaImpuestosQC.credits.tableAmount')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px', display: { xs: 'none', sm: 'table-cell' } }}>{t('blog.guiaImpuestosQC.credits.tableRequirement')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '12px' }}>{t('blog.guiaImpuestosQC.credits.tableFrequency')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {CREDITS_TABLE.map((row) => (
                      <TableRow key={row.credit}>
                        <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{t(`blog.guiaImpuestosQC.credits.${row.credit}`)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', fontFeatureSettings: "'tnum'" }}>{t(`blog.guiaImpuestosQC.credits.${row.amt}`)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem', display: { xs: 'none', sm: 'table-cell' } }}>{t(`blog.guiaImpuestosQC.credits.${row.req}`)}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem' }}>{t(`blog.guiaImpuestosQC.credits.${row.freq}`)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §6 — Ingresos del exterior (NEW Section C)
            ══════════════════════════════════════════════════════════ */}
            <Box id="ingresos-exterior" ref={setSectionRef('ingresos-exterior')} sx={{ ...sectionCard, mb: 6 }}>
              <Typography variant="h4" component="h2" sx={sectionHeading}>
                {t('blog.guiaImpuestosQC.foreignIncome.heading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.foreignIncome.intro')}
              </Typography>

              {/* Declare before/after */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.foreignIncome.declareHeading')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.875rem' }}>
                  {t('blog.guiaImpuestosQC.foreignIncome.declareBefore')}
                </Typography>
                <Typography sx={{ ...contentParagraph, mb: 1.5 }}>
                  {t('blog.guiaImpuestosQC.foreignIncome.declareBeforeText')}
                </Typography>
                <Typography sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.875rem' }}>
                  {t('blog.guiaImpuestosQC.foreignIncome.declareAfter')}
                </Typography>
                <Typography sx={contentParagraph}>
                  {t('blog.guiaImpuestosQC.foreignIncome.declareAfterText')}
                </Typography>
              </Box>

              {/* Double taxation */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.foreignIncome.doubleHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.foreignIncome.doubleText')}
              </Typography>
              <Typography sx={{ ...contentParagraph, mb: 0.5 }}>
                {t('blog.guiaImpuestosQC.foreignIncome.doubleNeed')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.foreignIncome.doubleDoc1')}</li>
                <li>{t('blog.guiaImpuestosQC.foreignIncome.doubleDoc2')}</li>
              </Box>

              {/* Foreign accounts */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.foreignIncome.accountsHeading')}
              </Typography>
              <Typography sx={contentParagraph}>
                {t('blog.guiaImpuestosQC.foreignIncome.accountsText')}
              </Typography>
              <Box sx={{ bgcolor: '#FFF8E1', borderRadius: '8px', p: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#7D5A00', fontWeight: 500 }}>
                  {t('blog.guiaImpuestosQC.foreignIncome.accountsNote')}
                </Typography>
              </Box>

              {/* Foreign property */}
              <Typography variant="h6" component="h3" sx={subHeading}>
                {t('blog.guiaImpuestosQC.foreignIncome.propertyHeading')}
              </Typography>
              <Typography sx={{ ...contentParagraph, mb: 1 }}>
                {t('blog.guiaImpuestosQC.foreignIncome.propertyIntro')}
              </Typography>
              <Box component="ul" sx={bulletList}>
                <li>{t('blog.guiaImpuestosQC.foreignIncome.propertyResidence')}</li>
                <li>{t('blog.guiaImpuestosQC.foreignIncome.propertyRental')}</li>
                <li>{t('blog.guiaImpuestosQC.foreignIncome.propertySale')}</li>
              </Box>
              <Box sx={{ bgcolor: '#E8F5E9', borderRadius: '8px', p: 2 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 500 }}>
                  <strong>Tip:</strong> {t('blog.guiaImpuestosQC.foreignIncome.propertyTip')}
                </Typography>
              </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §7 — Errores comunes (NEW Section D)
            ══════════════════════════════════════════════════════════ */}
            <Box id="errores" ref={setSectionRef('errores')} sx={{ ...sectionCard, mb: 6 }}>
              <Typography variant="h4" component="h2" sx={sectionHeading}>
                {t('blog.guiaImpuestosQC.mistakes.heading')}
              </Typography>

              {/* Error 1 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h3" sx={{ ...subHeading, mt: 1 }}>
                  {t('blog.guiaImpuestosQC.mistakes.error1Title')}
                </Typography>
                <Typography sx={{ fontStyle: 'italic', color: '#5F6B7A', fontSize: '0.875rem', mb: 1 }}>
                  {t('blog.guiaImpuestosQC.mistakes.error1Quote')}
                </Typography>
                <Typography sx={contentParagraph}>
                  {t('blog.guiaImpuestosQC.mistakes.error1Text')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#C62828' }}>
                  <WarningIcon sx={{ fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    {t('blog.guiaImpuestosQC.mistakes.error1Cost')}
                  </Typography>
                </Box>
              </Box>

              {/* Error 2 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h3" sx={subHeading}>
                  {t('blog.guiaImpuestosQC.mistakes.error2Title')}
                </Typography>
                <Typography sx={contentParagraph}>
                  {t('blog.guiaImpuestosQC.mistakes.error2Text')}
                </Typography>
                <Typography sx={{ ...contentParagraph, fontWeight: 500, color: '#1E3A5F' }}>
                  {t('blog.guiaImpuestosQC.mistakes.error2Tip')}
                </Typography>
              </Box>

              {/* Error 3 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h3" sx={subHeading}>
                  {t('blog.guiaImpuestosQC.mistakes.error3Title')}
                </Typography>
                <Typography sx={contentParagraph}>
                  {t('blog.guiaImpuestosQC.mistakes.error3Text')}
                </Typography>
              </Box>

              {/* Error 4 */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h3" sx={subHeading}>
                  {t('blog.guiaImpuestosQC.mistakes.error4Title')}
                </Typography>
                <Typography sx={contentParagraph}>
                  {t('blog.guiaImpuestosQC.mistakes.error4Text')}
                  {' '}
                  <RouterLink
                    to={`/${lng}/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes`}
                    style={{ color: '#1E3A5F', textDecoration: 'underline' }}
                  >
                    TFSA, RRSP y FHSA
                  </RouterLink>
                </Typography>
              </Box>

              {/* Error 5 */}
              <Box>
                <Typography variant="h6" component="h3" sx={subHeading}>
                  {t('blog.guiaImpuestosQC.mistakes.error5Title')}
                </Typography>
                <Typography sx={contentParagraph}>
                  {t('blog.guiaImpuestosQC.mistakes.error5Text')}
                </Typography>
              </Box>
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §8 — Refund Estimator
            ══════════════════════════════════════════════════════════ */}
            <Box id="reembolso" ref={setSectionRef('reembolso')} sx={{ ...sectionCard, mb: 6 }}>
              <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mb: 0.5 }}>
                {t('blog.guiaImpuestosQC.estimator.heading')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3 }}>
                {t('blog.guiaImpuestosQC.estimator.disclaimerBefore')}<RouterLink to={`/${lng}/blog/solidarity-tax-credit-quebec-guia-completa`} style={{ color: '#1E3A5F', textDecoration: 'underline' }}>{t('blog.guiaImpuestosQC.estimator.disclaimerLink')}</RouterLink>{t('blog.guiaImpuestosQC.estimator.disclaimerAfter')}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('blog.guiaImpuestosQC.estimator.incomeLabel')}
                    placeholder="50000"
                    type="number"
                    value={estIncome}
                    onChange={(e) => { setEstIncome(e.target.value); setEstResult(null); }}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: '#F7F9FC' } }}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Button
                    fullWidth
                    variant={estRent ? 'contained' : 'outlined'}
                    onClick={() => { setEstRent(!estRent); setEstResult(null); }}
                    sx={{
                      height: '40px',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 500,
                      ...(estRent
                        ? { bgcolor: '#1E3A5F', color: '#fff', '&:hover': { bgcolor: '#1A3152' } }
                        : { borderColor: '#E6E9EE', color: '#5F6B7A', '&:hover': { borderColor: '#1E3A5F', bgcolor: 'rgba(30,58,95,0.04)' } }),
                    }}
                  >
                    {estRent ? '\u2713 ' : ''}{t('blog.guiaImpuestosQC.estimator.payRent')}
                  </Button>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Button
                    fullWidth
                    variant={estRRSP ? 'contained' : 'outlined'}
                    onClick={() => { setEstRRSP(!estRRSP); setEstResult(null); }}
                    sx={{
                      height: '40px',
                      borderRadius: '10px',
                      textTransform: 'none',
                      fontWeight: 500,
                      ...(estRRSP
                        ? { bgcolor: '#1E3A5F', color: '#fff', '&:hover': { bgcolor: '#1A3152' } }
                        : { borderColor: '#E6E9EE', color: '#5F6B7A', '&:hover': { borderColor: '#1E3A5F', bgcolor: 'rgba(30,58,95,0.04)' } }),
                    }}
                  >
                    {estRRSP ? '\u2713 ' : ''}{t('blog.guiaImpuestosQC.estimator.contributedRRSP')}
                  </Button>
                </Grid>
              </Grid>

              {!estResult ? (
                <Button
                  variant="contained"
                  onClick={() => setEstResult(estimateRefund(estIncome, estRent, estRRSP, t))}
                  disabled={!estIncome}
                  sx={{
                    bgcolor: '#1E3A5F',
                    color: '#fff',
                    fontWeight: 500,
                    borderRadius: '12px',
                    px: 4,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1A3152' },
                    '&.Mui-disabled': { bgcolor: '#E6E9EE', color: '#5F6B7A' },
                  }}
                >
                  {t('blog.guiaImpuestosQC.estimator.calculate')}
                </Button>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Box
                    sx={{
                      bgcolor: '#F7F9FC',
                      borderRadius: '12px',
                      border: '1px solid #E6E9EE',
                      p: 3,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" sx={{ color: '#5F6B7A', fontWeight: 500 }}>
                        {t('blog.guiaImpuestosQC.estimator.estimatedRefund')}
                      </Typography>
                      <Typography variant="h4" sx={{ color: '#1E3A5F', fontFeatureSettings: "'tnum'" }}>
                        ~${estResult.total.toLocaleString()}
                      </Typography>
                    </Box>
                    {estResult.breakdown.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          py: 0.75,
                          '&:not(:last-child)': { borderBottom: '1px solid #E6E9EE' },
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1A1A1A', fontWeight: 600, fontFeatureSettings: "'tnum'" }}>
                          ${item.amount.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block', mt: 1.5 }}>
                    {t('blog.guiaImpuestosQC.estimator.footnote')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* ══════════════════════════════════════════════════════════
                §9 — FAQ (Fix: with new FAQs)
            ══════════════════════════════════════════════════════════ */}
            <Box id="faq" ref={setSectionRef('faq')} sx={{ mb: 6 }}>
              <Typography variant="h4" component="h2" sx={sectionHeading}>
                {t('blog.guiaImpuestosQC.faq.heading')}
              </Typography>
              {faqItems.map((faq, index) => (
                <Accordion
                  key={index}
                  disableGutters
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#1E3A5F' }} />}
                    sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
                  >
                    <Typography sx={{ fontWeight: 600, color: '#1A1A1A' }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                    <Typography variant="body1" sx={{ color: '#5F6B7A', lineHeight: 1.7 }}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            {/* ── Sources (Fix 6) ── */}
            <Box
              sx={{
                ...sectionCard,
                bgcolor: '#F7F9FC',
                mb: 6,
              }}
            >
              <Typography variant="h6" component="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1.5 }}>
                {t('blog.guiaImpuestosQC.sources.title')}
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 1.5, '& li': { mb: 0.5, fontSize: '0.875rem', color: '#5F6B7A' } }}>
                <li>
                  <a href="https://www.canada.ca/en/revenue-agency.html" target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A5F' }}>
                    CRA - Canada Revenue Agency
                  </a>
                </li>
                <li>
                  <a href="https://www.revenuquebec.ca/" target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A5F' }}>
                    Revenu Qu&eacute;bec
                  </a>
                </li>
                <li>
                  <a href="https://www.canada.ca/en/revenue-agency/services/child-family-benefits/gsthstc.html" target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A5F' }}>
                    GST/HST Credit - CRA
                  </a>
                </li>
              </Box>
              <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                {t('blog.guiaImpuestosQC.sources.updated')}
              </Typography>
            </Box>

            {/* ── Next reads ── */}
            <Typography variant="h4" component="h2" sx={{ color: '#1A1A1A', mb: 2.5 }}>
              {t('blog.guiaImpuestosQC.nextReads.heading')}
            </Typography>
            <Grid container spacing={2.5} sx={{ mb: 2 }}>
              {NEXT_READS.map((card) => (
                <Grid item xs={12} sm={4} key={card.title}>
                  <Card sx={cardSx}>
                    <CardActionArea
                      component={RouterLink}
                      to={`/${lng}${card.to}`}
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        <Box sx={{ color: '#1E3A5F', mb: 1.5 }}>{card.icon}</Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 0.5, lineHeight: 1.35 }}>
                          {card.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, mb: 1.5 }}>
                          {card.desc}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1E3A5F', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                          {t('blog.guiaImpuestosQC.nextReads.read')} <ArrowForward sx={{ fontSize: 14 }} />
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <RelatedArticles slug="guia-impuestos-latinos-quebec-2026" />
          </Grid>
        </Grid>
      </Container>

      <NewsletterSection />
    </Box>
  );
};

export default GuiaImpuestosQuebecPage;
